import clsx from 'clsx';

import { usePreferences } from '@atom/hooks/usePreferences';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Icon, IconButton, Tooltip } from '@atom/mui';
import colors from '@atom/styles/colors';
import { TimeEntryStatus, WorkOrderTimeEntry } from '@atom/types/timeEntry';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import {
  convertMillisToExactHours,
  setDisplayDate,
} from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './userTimeEntryModal.module.css';

interface Props {
  timeEntry: WorkOrderTimeEntry;
  isTimeEntryDisabled: boolean;
  editTimeEntryId: string;
  setEditTimeEntryId: (id: string) => void;
  handleTimeEntryDelete: (id: string) => void;
}

const UserTimeEntryDisplayRow = ({
  timeEntry,
  isTimeEntryDisabled,
  handleTimeEntryDelete,
  editTimeEntryId,
  setEditTimeEntryId,
}: Props) => {
  const userProfile = useUserProfile();
  const preferences = usePreferences();

  const budgetNames = timeEntry.computedBudgets
    .map(budget => `${budget.name}${budget.restored ? ' (snapshot)' : ''}`)
    .join(', ');

  const showTooltip = timeEntry.computedBudgets.length > 1;

  const budgetsTooltipTitle = (
    <div className={classes.tooltipContainer}>
      {timeEntry.computedBudgets.map(budget => (
        <div key={budget.id} className={classes.tooltipRow}>
          <span>
            {`${budget.name}: `}
            <strong>{`${convertMillisToExactHours(
              budget.duration,
            )} Hour(s)`}</strong>
            {budget.restored && (
              <span className={classes.snapshotSuffix}>(snapshot)</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );

  const workTimeStyle = showTooltip
    ? { borderBottom: `1px dotted ${colors.neutral.gray}` }
    : {};

  const isEditDisabled =
    isTimeEntryDisabled ||
    !!editTimeEntryId ||
    timeEntry.status === TimeEntryStatus.approved ||
    (timeEntry.userId !== userProfile.userId &&
      doesNotHaveRolePermissions(ROLE_SETS.MANAGER));
  const isDeleteDisabled =
    isTimeEntryDisabled ||
    timeEntry.status === TimeEntryStatus.approved ||
    (timeEntry.userId !== userProfile.userId &&
      doesNotHaveRolePermissions(ROLE_SETS.MANAGER));

  const duration = convertMillisToExactHours(timeEntry.duration);
  const hoursLabel = duration === 1 ? 'Hour' : 'Hours';

  return (
    <div className={classes.timeEntryRow}>
      <div className={classes.timeEntryDataSection}>
        <div className={classes.displayContainer}>
          <div className={classes.label}>Start Date</div>
          <div>{setDisplayDate(timeEntry?.date) || '-'}</div>
        </div>
        {preferences?.timeTracking?.timesheet && (
          <div className={classes.displayContainer}>
            <div className={classes.label}>Group</div>
            <div>{timeEntry?.userGroup?.name || '-'}</div>
          </div>
        )}
        <div className={clsx(classes.displayContainer, classes.workTime)}>
          <div className={classes.label}>Work Time</div>
          {showTooltip ? (
            <Tooltip lightTooltip title={budgetsTooltipTitle}>
              <div style={workTimeStyle}>{`${duration} ${hoursLabel}`}</div>
            </Tooltip>
          ) : (
            <div>{`${duration} ${hoursLabel}`}</div>
          )}
        </div>
        <div className={classes.displayContainer}>
          <div className={classes.label}>
            {isNilOrEmpty(preferences?.timeTracking?.typeEnumeration)
              ? 'Budget'
              : 'Wage Type'}
          </div>
          <div>{budgetNames || '-'}</div>
        </div>
      </div>
      <div className={classes.timeEntryButtonSection}>
        <IconButton
          tooltip="Edit"
          onClick={() => setEditTimeEntryId(timeEntry.id)}
          disabled={isEditDisabled}
        >
          <Icon>edit</Icon>
        </IconButton>
        <Tooltip
          title={
            timeEntry.status === TimeEntryStatus.approved
              ? 'Approved time cannot be deleted.'
              : 'Delete'
          }
          lightTooltip={timeEntry.status === TimeEntryStatus.approved}
        >
          <span>
            <IconButton
              onClick={() => handleTimeEntryDelete(timeEntry.id)}
              disabled={isDeleteDisabled}
            >
              <Icon>delete</Icon>
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default UserTimeEntryDisplayRow;
